import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, Statistic, Table, Typography, Alert, Spin, Empty } from "antd";

const { Text } = Typography;

// Absence-rate colour: severe (>=50) red, elevated (>=20) amber, else green.
const absenceRateColor = (rate) => {
  if (rate == null) return undefined;
  if (rate >= 50) return "#cf1322";
  if (rate >= 20) return "#d46b08";
  return "#389e0d";
};

const flagColumns = [
  { title: "Employee", dataIndex: "employeeName", key: "emp", render: (v) => v || "-" },
  { title: "Department", dataIndex: "department", key: "dept", render: (v) => v || "-" },
  { title: "Observation", dataIndex: "observation", key: "obs", render: (v) => v || "-" },
  { title: "Suggested Action", dataIndex: "suggestedAction", key: "act", render: (v) => v || "-" },
];

const perEmployeeColumns = [
  { title: "Employee", dataIndex: "employeeName", key: "pe-emp", render: (v) => v || "-" },
  { title: "Department", dataIndex: "department", key: "pe-dept", render: (v) => v || "-" },
  { title: "Absent (days)", dataIndex: "absent", key: "pe-absent", render: (v) => v ?? 0 },
  { title: "Late (days)", dataIndex: "late", key: "pe-late", render: (v) => v ?? 0 },
  { title: "Early Leave (days)", dataIndex: "earlyLeave", key: "pe-early", render: (v) => v ?? 0 },
];

// Shared AI attendance-insights renderer used by both the manager (Team
// Attendance) and HR/admin (Attendance) screens. The parent owns the filters
// (date range, plus a department picker for admin) and passes the fetched
// payload + loading here. Advisory / read-only.
function AttendanceInsightsView({ insights, loading, emptyText }) {
  const agg = insights?.requestPayload?.teamAggregates;
  const baseline = insights?.requestPayload?.baseline;
  const aiInsight = insights?.insight;
  const perEmployee = insights?.requestPayload?.perEmployee;

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (!insights) {
    return <Empty description={emptyText || "Select a date range to view insights"} />;
  }

  return (
    <>
      {agg && agg.totalEmployees > 0 && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic title="Employees" value={agg.totalEmployees ?? 0} />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="Absent (days)"
                value={agg.absentCount ?? 0}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="Late (days)"
                value={agg.lateCount ?? 0}
                valueStyle={{ color: "#d46b08" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="Early Leave (days)"
                value={agg.earlyLeaveCount ?? 0}
                valueStyle={{ color: "#d4b106" }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card size="small">
              <Statistic
                title="Absence Rate"
                value={agg.absenceRatePct ?? 0}
                suffix="%"
                valueStyle={{ color: absenceRateColor(agg.absenceRatePct) }}
              />
              {baseline && baseline.absenceRatePct != null && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Baseline {baseline.absenceRatePct}%
                </Text>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {perEmployee && perEmployee.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>Per-employee breakdown</Text>
          <Table
            className="custom-table"
            style={{ marginTop: 8 }}
            size="small"
            columns={perEmployeeColumns}
            dataSource={perEmployee.map((e, i) => ({ ...e, key: i }))}
            pagination={false}
            scroll={{ x: 600 }}
          />
        </div>
      )}

      {insights.isAiInsightAvailable && aiInsight ? (
        <>
          <Card
            size="small"
            style={{ marginBottom: 16, background: "#f6faf6", border: "1px solid #d9f7be" }}
          >
            <Text strong style={{ fontSize: 16, display: "block", marginBottom: 12 }}>
              {aiInsight.headline}
            </Text>

            {Array.isArray(aiInsight.patterns) && aiInsight.patterns.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Text strong>Patterns</Text>
                <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                  {aiInsight.patterns.map((p, i) => (
                    <li key={i} style={{ marginBottom: 6 }}>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {aiInsight.likelyImpact && (
              <div>
                <Text strong>Likely Impact</Text>
                <p style={{ marginTop: 6, marginBottom: 0 }}>{aiInsight.likelyImpact}</p>
              </div>
            )}
          </Card>

          {Array.isArray(aiInsight.flags) && aiInsight.flags.length > 0 && (
            <>
              <Text strong>Flags</Text>
              <Table
                className="custom-table"
                style={{ marginTop: 8 }}
                size="small"
                columns={flagColumns}
                dataSource={aiInsight.flags.map((f, i) => ({ ...f, key: i }))}
                pagination={false}
                scroll={{ x: 700 }}
              />
            </>
          )}
        </>
      ) : (
        <Alert
          type="info"
          showIcon
          message={insights.message || "AI insights are not available right now."}
        />
      )}
    </>
  );
}

AttendanceInsightsView.propTypes = {
  insights: PropTypes.object,
  loading: PropTypes.bool,
  emptyText: PropTypes.string,
};

export default AttendanceInsightsView;
